import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { uploadImageAndGetUrl, deleteImage, getSignedImageUrl, getBucketForCategory } from '../../../utils/imageUpload';
import { aboutService } from '../../../services/artistContentService';
import { useAuth } from '../../../contexts/AuthContext';

const AboutSection = ({ artistData, onUpdate, isActive }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    headerImage: null,
    headerImagePath: null,
    aboutImage: null,
    aboutImagePath: null,
    aboutText: '',
    bookingContact: {
      name: '',
      email: '',
      phone: ''
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [dragActiveAbout, setDragActiveAbout] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAbout, setUploadingAbout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState('');
  const [imageLoadRetries, setImageLoadRetries] = useState(0);
  const fileRef = useRef(null);
  const aboutFileRef = useRef(null);
  const maxRetries = 3;

  // Load About content on component mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadAboutContent();
    } else {
      // Reset form when user logs out
      setFormData({
        headerImage: null,
        headerImagePath: null,
        aboutImage: null,
        aboutImagePath: null,
        aboutText: '',
        bookingContact: {
          name: '',
          email: '',
          phone: ''
        }
      });
      setLoading(false);
    }
  }, [user?.id]);

  // Also reload when artistData.about changes from useArtistData hook
  useEffect(() => {
    if (user?.id && artistData?.about && !loading) {
      setFormData(prev => ({
        ...prev,
        headerImage: artistData?.about?.headerImage || prev?.headerImage,
        headerImagePath: artistData?.about?.headerImagePath || prev?.headerImagePath,
        aboutImage: artistData?.about?.aboutImage || prev?.aboutImage,
        aboutImagePath: artistData?.about?.aboutImagePath || prev?.aboutImagePath,
        aboutText: artistData?.about?.aboutText || prev?.aboutText,
        bookingContact: artistData?.about?.bookingContact || prev?.bookingContact
      }));
    }
  }, [artistData?.about, user?.id, loading]);

  const loadAboutContent = async () => {
    if (!user?.id) return;

    setLoading(true);
    setSaveError('');
    setImageLoadRetries(0);
    
    try {
      const result = await aboutService?.getAboutContent(user?.id);
      let data = null;
      let error = null;
      
      // Enhanced response format handling
      if (result && typeof result === 'object') {
        if ('data' in result && 'error' in result) {
          // Service returns { data, error } format
          ({ data, error } = result);
        } else if (result?.id || result === null) {
          // Service returns data directly
          data = result;
        } else {
          // Unknown format, treat as error
          error = new Error('Unknown response format from service');
        }
      } else {
        // Service returns data directly
        data = result;
      }
      
      if (error && error?.code !== 'PGRST116') { // PGRST116 = not found
        console.error('Error loading about content:', error);
        setSaveError('Failed to load about content. Please refresh the page.');
        return;
      }

      if (data) {
        // Enhanced image URL handling with retry mechanism
        let headerImageUrl = null;
        if (data?.header_image_path) {
          headerImageUrl = await loadImageWithRetry(data?.header_image_path);
        }

        let aboutImageUrl = null;
        if (data?.about_image_path) {
          aboutImageUrl = await loadImageWithRetry(data?.about_image_path);
        }

        const newFormData = {
          headerImage: headerImageUrl,
          headerImagePath: headerImageUrl ? data?.header_image_path : null,
          aboutImage: aboutImageUrl,
          aboutImagePath: aboutImageUrl ? data?.about_image_path : null,
          aboutText: data?.about_text || '',
          bookingContact: data?.booking_contact || {
            name: '',
            email: '',
            phone: ''
          }
        };

        setFormData(newFormData);

        // Update parent component to sync with useArtistData
        onUpdate?.('about', newFormData);
      } else {
        // No data exists, set defaults
        const defaultData = {
          headerImage: null,
          headerImagePath: null,
          aboutImage: null,
          aboutImagePath: null,
          aboutText: '',
          bookingContact: {
            name: '',
            email: '',
            phone: ''
          }
        };
        setFormData(defaultData);
      }
    } catch (error) {
      console.error('Error loading about content:', error);
      setSaveError('Failed to load about content. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced image loading with retry mechanism
  const loadImageWithRetry = async (imagePath, retryCount = 0) => {
    if (!imagePath || retryCount >= maxRetries) {
      if (retryCount >= maxRetries) {
        console.warn(`Max retries (${maxRetries}) reached for image: ${imagePath}`);
      }
      return null;
    }

    try {
      const bucket = getBucketForCategory('profile');
      const urlResult = await getSignedImageUrl(bucket, imagePath, 7200);
      
      if (urlResult?.data?.signedUrl) {
        return urlResult?.data?.signedUrl;
      } else if (urlResult?.error) {
        // Handle "Object not found" errors gracefully
        if (urlResult?.error?.includes('not found') || urlResult?.error?.includes('Object not found')) {
          console.warn(`Image not found in storage: ${imagePath}. Image may have been moved or deleted.`);
          // Clear the invalid path from database to prevent future errors
          try {
            await aboutService?.saveAboutContent(user?.id, {
              headerImagePath: null,
              aboutText: formData?.aboutText || '',
              bookingContact: formData?.bookingContact || { name: '', email: '', phone: '' }
            });
          } catch (clearError) {
            console.warn('Could not clear invalid image path:', clearError);
          }
          return null;
        } else {
          console.warn(`Error generating signed URL (attempt ${retryCount + 1}/${maxRetries}):`, urlResult?.error);
          // Retry after a short delay
          if (retryCount < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Progressive delay
            return loadImageWithRetry(imagePath, retryCount + 1);
          } else {
            setSaveError('Image loading issue. Please try refreshing the page.');
            return null;
          }
        }
      } else {
        console.warn('Unexpected response format from image URL service');
        return null;
      }
    } catch (error) {
      console.warn(`Error loading image (attempt ${retryCount + 1}/${maxRetries}):`, error);
      // Retry on network or other errors
      if (retryCount < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Progressive delay
        return loadImageWithRetry(imagePath, retryCount + 1);
      } else {
        console.error('Final attempt failed for image:', imagePath, error);
        return null;
      }
    }
  };

  const handleImageUpload = async (file) => {
    if (!file || !file?.type?.startsWith('image/') || !user?.id) {
      if (file && !file?.type?.startsWith('image/')) {
        setSaveError('Please select a valid image file (JPG, PNG, etc.)');
      }
      return;
    }

    setUploading(true);
    setSaveError('');

    try {
      // Delete old image if it exists
      if (formData?.headerImagePath) {
        try {
          const bucket = getBucketForCategory('profile');
          await deleteImage(bucket, formData?.headerImagePath);
        } catch (deleteError) {
          console.warn('Could not delete old image:', deleteError);
          // Continue with upload even if delete fails
        }
      }

      // Upload new image
      const result = await uploadImageAndGetUrl(file, user?.id, 'profile');
      
      if (result?.error) {
        setSaveError(`Upload failed: ${result?.error}`);
        return;
      }

      if (!result?.data?.url || !result?.data?.path) {
        setSaveError('Upload failed: Invalid response from upload service');
        return;
      }

      // Immediately update form data with new image
      const newFormData = {
        ...formData,
        headerImage: result?.data?.url,
        headerImagePath: result?.data?.path
      };
      
      setFormData(newFormData);

      // Auto-save the new image path to database immediately
      const saveResult = await aboutService?.saveAboutContent(user?.id, {
        headerImagePath: result?.data?.path,
        aboutText: formData?.aboutText,
        bookingContact: formData?.bookingContact
      });

      // Enhanced save result handling
      let saveData = null;
      let saveError = null;
      
      if (saveResult && typeof saveResult === 'object') {
        if ('data' in saveResult && 'error' in saveResult) {
          ({ data: saveData, error: saveError } = saveResult);
        } else {
          saveData = saveResult;
        }
      } else {
        saveData = saveResult;
      }

      if (saveError) {
        console.warn('Auto-save warning:', saveError);
        setSaveError('Image uploaded but not saved to database. Please click Save Changes.');
      } else {
        // Update parent component with new data immediately
        onUpdate?.('about', newFormData);
        console.log('Image uploaded and saved successfully');
        
        // Show brief success message
        setSaveError('Image uploaded successfully!');
        setTimeout(() => {
          setSaveError(prev => prev === 'Image uploaded successfully!' ? '' : prev);
        }, 3000);
      }

    } catch (error) {
      console.error('Image upload error:', error);
      setSaveError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragActive(false);
    
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleImageUpload(files?.[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Clear the input so the same file can be selected again if needed
    if (e?.target) {
      e.target.value = '';
    }
  };

  // Click handler to trigger file input
  const handleUploadClick = () => {
    if (isEditing && fileRef?.current && !uploading) {
      fileRef?.current?.click();
    }
  };

  const removeImage = async () => {
    if (!isEditing) return;

    setLoading(true);
    try {
      // Delete from storage if path exists
      if (formData?.headerImagePath) {
        try {
          const bucket = getBucketForCategory('profile');
          await deleteImage(bucket, formData?.headerImagePath);
        } catch (deleteError) {
          console.warn('Could not delete image from storage:', deleteError);
        }
      }

      // Update database to remove image path
      await aboutService?.saveAboutContent(user?.id, {
        headerImagePath: null,
        aboutText: formData?.aboutText,
        bookingContact: formData?.bookingContact
      });
      
      // Update form data
      const newFormData = {
        ...formData,
        headerImage: null,
        headerImagePath: null
      };
      
      setFormData(newFormData);
      onUpdate?.('about', newFormData);
      
    } catch (error) {
      console.error('Error removing image:', error);
      setSaveError('Failed to remove image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // About Image upload handlers
  const handleAboutImageUpload = async (file) => {
    if (!file || !file?.type?.startsWith('image/') || !user?.id) {
      if (file && !file?.type?.startsWith('image/')) {
        setSaveError('Please select a valid image file (JPG, PNG, etc.)');
      }
      return;
    }

    setUploadingAbout(true);
    setSaveError('');

    try {
      if (formData?.aboutImagePath) {
        try {
          const bucket = getBucketForCategory('profile');
          await deleteImage(bucket, formData?.aboutImagePath);
        } catch (deleteError) {
          console.warn('Could not delete old about image:', deleteError);
        }
      }

      const result = await uploadImageAndGetUrl(file, user?.id, 'profile');

      if (result?.error) {
        setSaveError(`Upload failed: ${result?.error}`);
        return;
      }

      if (!result?.data?.url || !result?.data?.path) {
        setSaveError('Upload failed: Invalid response from upload service');
        return;
      }

      const newFormData = {
        ...formData,
        aboutImage: result?.data?.url,
        aboutImagePath: result?.data?.path
      };

      setFormData(newFormData);

      const saveResult = await aboutService?.saveAboutContent(user?.id, {
        headerImagePath: formData?.headerImagePath,
        aboutImagePath: result?.data?.path,
        aboutText: formData?.aboutText,
        bookingContact: formData?.bookingContact
      });

      let saveData = null;
      let saveErr = null;

      if (saveResult && typeof saveResult === 'object') {
        if ('data' in saveResult && 'error' in saveResult) {
          ({ data: saveData, error: saveErr } = saveResult);
        } else {
          saveData = saveResult;
        }
      } else {
        saveData = saveResult;
      }

      if (saveErr) {
        console.warn('Auto-save warning:', saveErr);
        setSaveError('Image uploaded but not saved to database. Please click Save Changes.');
      } else {
        onUpdate?.('about', newFormData);
        setSaveError('About image uploaded successfully!');
        setTimeout(() => {
          setSaveError(prev => prev === 'About image uploaded successfully!' ? '' : prev);
        }, 3000);
      }
    } catch (error) {
      console.error('About image upload error:', error);
      setSaveError('Failed to upload about image. Please try again.');
    } finally {
      setUploadingAbout(false);
    }
  };

  const handleAboutDragOver = (e) => {
    e?.preventDefault();
    setDragActiveAbout(true);
  };

  const handleAboutDragLeave = (e) => {
    e?.preventDefault();
    setDragActiveAbout(false);
  };

  const handleAboutDrop = (e) => {
    e?.preventDefault();
    setDragActiveAbout(false);
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleAboutImageUpload(files?.[0]);
    }
  };

  const handleAboutFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleAboutImageUpload(file);
    }
    if (e?.target) {
      e.target.value = '';
    }
  };

  const handleAboutUploadClick = () => {
    if (isEditing && aboutFileRef?.current && !uploadingAbout) {
      aboutFileRef?.current?.click();
    }
  };

  const removeAboutImage = async () => {
    if (!isEditing) return;

    setLoading(true);
    try {
      if (formData?.aboutImagePath) {
        try {
          const bucket = getBucketForCategory('profile');
          await deleteImage(bucket, formData?.aboutImagePath);
        } catch (deleteError) {
          console.warn('Could not delete about image from storage:', deleteError);
        }
      }

      await aboutService?.saveAboutContent(user?.id, {
        headerImagePath: formData?.headerImagePath,
        aboutImagePath: null,
        aboutText: formData?.aboutText,
        bookingContact: formData?.bookingContact
      });

      const newFormData = {
        ...formData,
        aboutImage: null,
        aboutImagePath: null
      };

      setFormData(newFormData);
      onUpdate?.('about', newFormData);
    } catch (error) {
      console.error('Error removing about image:', error);
      setSaveError('Failed to remove about image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (value) => {
    setFormData(prev => ({
      ...prev,
      aboutText: value
    }));
  };

  const handleContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      bookingContact: {
        ...prev?.bookingContact,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      setSaveError('Please log in to save changes.');
      return;
    }

    setSaveError('');
    setLoading(true);
    
    try {
      let saveData = {
        headerImagePath: formData?.headerImagePath,
        aboutImagePath: formData?.aboutImagePath,
        aboutText: formData?.aboutText,
        bookingContact: formData?.bookingContact
      };

      const result = await aboutService?.saveAboutContent(user?.id, saveData);
      let data = null;
      let error = null;
      
      // Enhanced response format handling for save operation
      if (result && typeof result === 'object') {
        if ('data' in result && 'error' in result) {
          ({ data, error } = result);
        } else {
          data = result;
        }
      } else {
        data = result;
      }

      if (error) {
        setSaveError(`Save failed: ${error?.message || 'Unknown error'}`);
        return;
      }

      // After successful save, reload the content to ensure UI consistency
      await loadAboutContent();
      setIsEditing(false);
      
      // Show brief success message
      setSaveError('Changes saved successfully!');
      setTimeout(() => {
        if (saveError === 'Changes saved successfully!') {
          setSaveError('');
        }
      }, 3000);

    } catch (error) {
      setSaveError('Failed to save about content. Please try again.');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reload content to reset form
    loadAboutContent();
    setIsEditing(false);
    setSaveError('');
  };

  // Enhanced image error handler with retry functionality
  const handleImageError = () => {
    console.warn('Image failed to load, attempting to reload with retry');
    
    if (imageLoadRetries < maxRetries && formData?.headerImagePath) {
      setImageLoadRetries(prev => prev + 1);
      // Try to reload the image with retry mechanism
      loadImageWithRetry(formData?.headerImagePath, imageLoadRetries)?.then(newUrl => {
        if (newUrl) {
          setFormData(prev => ({
            ...prev,
            headerImage: newUrl
          }));
        } else {
          // Clear the broken image after all retries
          setFormData(prev => ({
            ...prev,
            headerImage: null
          }));
          setSaveError('Image could not be loaded. It may have been moved or deleted.');
        }
      });
    } else {
      // Clear the broken image
      console.warn('Image failed to load after retries, clearing from display');
      setFormData(prev => ({
        ...prev,
        headerImage: null
      }));
      setSaveError('Image could not be loaded. Please try uploading again.');
    }
  };

  if (!isActive) return null;

  if (loading && !formData?.headerImage && !formData?.aboutText) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-text-secondary">Loading about content...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">About Section</h2>
            <p className="text-sm text-text-secondary">Manage your biography, header image, and booking contact information</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsEditing(true)}
              iconName="Edit"
              iconPosition="left"
              iconSize={16}
              disabled={loading}
            >
              Edit About
            </Button>
          )}
        </div>
      </div>

      {saveError && (
        <div className={`mb-4 p-3 rounded-lg ${
          saveError?.includes('successfully') 
            ? 'bg-success/10 border border-success/20' :'bg-destructive/10 border border-destructive/20'
        }`}>
          <div className="flex items-center justify-between">
            <p className={`text-sm ${
              saveError?.includes('successfully')
                ? 'text-success' :'text-destructive'
            }`}>
              {saveError}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSaveError('')}
              className={`h-auto p-1 ${
                saveError?.includes('successfully')
                  ? 'text-success hover:bg-success/10' :'text-destructive hover:bg-destructive/10'
              }`}
            >
              <Icon name="X" size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Header Image Upload */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Header Image</h3>
        
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
            dragActive 
              ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50'
          } ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          {formData?.headerImage ? (
            <div className="relative">
              <Image
                src={formData?.headerImage}
                alt="About section header"
                className="w-full h-48 object-cover rounded-lg"
                onError={handleImageError}
              />
              {isEditing && !uploading && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={(e) => {
                    e?.stopPropagation();
                    removeImage();
                  }}
                >
                  <Icon name="X" size={16} />
                </Button>
              )}
              
              {/* Retry button for failed images */}
              {imageLoadRetries > 0 && imageLoadRetries < maxRetries && (
                <div className="absolute bottom-2 right-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleImageError();
                    }}
                    className="bg-white/90 backdrop-blur-sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Retry ({imageLoadRetries}/{maxRetries})
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center">
              <Icon name="Image" size={32} className="mx-auto text-text-secondary mb-2" />
              <p className="text-sm text-text-primary mb-1">
                {isEditing 
                  ? "Drop your header image here or click to browse" :"Header image will appear here"
                }
              </p>
              <p className="text-xs text-text-secondary">
                {isEditing 
                  ? "Recommended: 1200x400px, JPG or PNG (Max 5MB)"
                  : "Click Edit About to upload an image"
                }
              </p>
            </div>
          )}
          
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <div className="flex items-center space-x-2 text-white">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Uploading...</span>
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
            tabIndex={-1}
          />
        </div>

        {/* Upload button for better accessibility */}
        {isEditing && !formData?.headerImage && (
          <div className="mt-3 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleUploadClick}
              disabled={uploading || loading}
              iconName="Upload"
              iconPosition="left"
              iconSize={16}
            >
              {uploading ? 'Uploading...' : 'Choose Image'}
            </Button>
          </div>
        )}
      </div>

      {/* About Image Upload */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">About Image</h3>
        
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
            dragActiveAbout 
              ? 'border-accent bg-accent/10' :'border-border hover:border-accent/50'
          } ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          onDragOver={handleAboutDragOver}
          onDragLeave={handleAboutDragLeave}
          onDrop={handleAboutDrop}
          onClick={handleAboutUploadClick}
        >
          {formData?.aboutImage ? (
            <div className="relative">
              <Image
                src={formData?.aboutImage}
                alt="About section image"
                className="w-full h-48 object-cover rounded-lg"
              />
              {isEditing && !uploadingAbout && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={(e) => {
                    e?.stopPropagation();
                    removeAboutImage();
                  }}
                >
                  <Icon name="X" size={16} />
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center">
              <Icon name="Image" size={32} className="mx-auto text-text-secondary mb-2" />
              <p className="text-sm text-text-primary mb-1">
                {isEditing 
                  ? "Drop your about image here or click to browse" :"About image will appear here"
                }
              </p>
              <p className="text-xs text-text-secondary">
                {isEditing 
                  ? "Recommended: 800x600px, JPG or PNG (Max 5MB)"
                  : "Click Edit About to upload an image"
                }
              </p>
            </div>
          )}
          
          {uploadingAbout && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <div className="flex items-center space-x-2 text-white">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Uploading...</span>
              </div>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={aboutFileRef}
            type="file"
            accept="image/*"
            onChange={handleAboutFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
            tabIndex={-1}
          />
        </div>

        {/* Upload button for better accessibility */}
        {isEditing && !formData?.aboutImage && (
          <div className="mt-3 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAboutUploadClick}
              disabled={uploadingAbout || loading}
              iconName="Upload"
              iconPosition="left"
              iconSize={16}
            >
              {uploadingAbout ? 'Uploading...' : 'Choose Image'}
            </Button>
          </div>
        )}
      </div>

      {/* About Text Editor */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Biography</h3>
        
        <div className="space-y-2">
          <label className="text-sm font-medium text-text-primary">
            About Text
          </label>
          <textarea
            value={formData?.aboutText}
            onChange={(e) => handleTextChange(e?.target?.value)}
            disabled={!isEditing || loading}
            rows={8}
            className={`w-full px-3 py-2 border border-border rounded-lg resize-none transition-colors ${
              isEditing && !loading
                ? 'bg-input text-text-primary focus:outline-none focus:ring-2 focus:ring-accent' :'bg-muted text-text-secondary cursor-not-allowed'
            }`}
            placeholder="Write your artist biography here..."
          />
          <p className="text-xs text-text-secondary">
            {formData?.aboutText?.length || 0} characters
          </p>
        </div>
      </div>

      {/* Booking Contact Information */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-medium text-text-primary mb-4">Booking Contact</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Contact Name"
            type="text"
            placeholder="Enter contact name"
            value={formData?.bookingContact?.name || ''}
            onChange={(e) => handleContactChange('name', e?.target?.value)}
            disabled={!isEditing || loading}
            required
          />
          
          <Input
            label="Contact Email"
            type="email"
            placeholder="Enter contact email"
            value={formData?.bookingContact?.email || ''}
            onChange={(e) => handleContactChange('email', e?.target?.value)}
            disabled={!isEditing || loading}
            required
          />
          
          <Input
            label="Contact Phone"
            type="tel"
            placeholder="Enter contact phone"
            value={formData?.bookingContact?.phone || ''}
            onChange={(e) => handleContactChange('phone', e?.target?.value)}
            disabled={!isEditing || loading}
          />
        </div>
      </div>

      {/* Status Indicator */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon 
            name={formData?.aboutText && formData?.bookingContact?.name && formData?.bookingContact?.email ? "CheckCircle" : "AlertCircle"} 
            size={16} 
            className={formData?.aboutText && formData?.bookingContact?.name && formData?.bookingContact?.email ? "text-success" : "text-warning"}
          />
          <span className="text-sm font-medium text-text-primary">
            About Status: {formData?.aboutText && formData?.bookingContact?.name && formData?.bookingContact?.email ? "Complete" : "Incomplete"}
          </span>
        </div>
        {(!formData?.aboutText || !formData?.bookingContact?.name || !formData?.bookingContact?.email) && (
          <p className="text-sm text-text-secondary mt-1">
            Please complete biography text and booking contact information.
          </p>
        )}
      </div>
    </div>
  );
};

export default AboutSection;